/**
 * Custom Report Builder Service
 * Allows users to create custom reports with selected fields and filters
 */

import { supabase } from './supabase'

/**
 * Available report data sources
 */
export const REPORT_SOURCES = {
  CLIENTS: 'clients',
  EXAMINATIONS: 'examinations',
  CARE_PLANS: 'care_plans',
  THERAPIES: 'therapies',
  SESSIONS: 'sessions',
  PROGRESS: 'care_plan_progress',
  OUTCOMES: 'care_plan_outcomes',
}

/**
 * Available field types for reports
 */
export const FIELD_TYPES = {
  TEXT: 'text',
  NUMBER: 'number',
  DATE: 'date',
  BOOLEAN: 'boolean',
  SELECT: 'select',
}

/**
 * Get available fields for a data source
 */
export const getAvailableFields = (source) => {
  const fieldMaps = {
    clients: [
      { name: 'first_name', label: 'First Name', type: FIELD_TYPES.TEXT },
      { name: 'last_name', label: 'Last Name', type: FIELD_TYPES.TEXT },
      { name: 'email', label: 'Email', type: FIELD_TYPES.TEXT },
      { name: 'phone', label: 'Phone', type: FIELD_TYPES.TEXT },
      { name: 'date_of_birth', label: 'Date of Birth', type: FIELD_TYPES.DATE },
      { name: 'gender', label: 'Gender', type: FIELD_TYPES.SELECT },
      { name: 'created_at', label: 'Created Date', type: FIELD_TYPES.DATE },
      { name: 'updated_at', label: 'Updated Date', type: FIELD_TYPES.DATE },
    ],
    examinations: [
      { name: 'id', label: 'Examination ID', type: FIELD_TYPES.TEXT },
      { name: 'client_id', label: 'Client ID', type: FIELD_TYPES.TEXT },
      { name: 'created_at', label: 'Examination Date', type: FIELD_TYPES.DATE },
      { name: 'vital_signs', label: 'Vital Signs', type: FIELD_TYPES.TEXT },
    ],
    care_plans: [
      { name: 'id', label: 'Care Plan ID', type: FIELD_TYPES.TEXT },
      { name: 'client_id', label: 'Client ID', type: FIELD_TYPES.TEXT },
      { name: 'title', label: 'Title', type: FIELD_TYPES.TEXT },
      { name: 'status', label: 'Status', type: FIELD_TYPES.SELECT },
      { name: 'start_date', label: 'Start Date', type: FIELD_TYPES.DATE },
      { name: 'end_date', label: 'End Date', type: FIELD_TYPES.DATE },
      { name: 'created_at', label: 'Created Date', type: FIELD_TYPES.DATE },
    ],
    therapies: [
      { name: 'id', label: 'Therapy ID', type: FIELD_TYPES.TEXT },
      { name: 'client_id', label: 'Client ID', type: FIELD_TYPES.TEXT },
      { name: 'therapy_type', label: 'Therapy Type', type: FIELD_TYPES.TEXT },
      { name: 'scheduled_date', label: 'Scheduled Date', type: FIELD_TYPES.DATE },
      { name: 'status', label: 'Status', type: FIELD_TYPES.SELECT },
    ],
    sessions: [
      { name: 'id', label: 'Session ID', type: FIELD_TYPES.TEXT },
      { name: 'client_id', label: 'Client ID', type: FIELD_TYPES.TEXT },
      { name: 'session_date', label: 'Session Date', type: FIELD_TYPES.DATE },
      { name: 'duration', label: 'Duration (minutes)', type: FIELD_TYPES.NUMBER },
    ],
  }

  return fieldMaps[source] || []
}

/**
 * Build and execute a custom report
 */
export const buildReport = async (reportConfig, organizationId) => {
  const { source, fields, filters, sortBy, limit } = reportConfig

  try {
    let query = supabase.from(source).select(fields.join(', ')).eq('organization_id', organizationId)

    // Apply filters
    if (filters && filters.length > 0) {
      filters.forEach((filter) => {
        switch (filter.operator) {
          case 'equals':
            query = query.eq(filter.field, filter.value)
            break
          case 'not_equals':
            query = query.neq(filter.field, filter.value)
            break
          case 'contains':
            query = query.ilike(filter.field, `%${filter.value}%`)
            break
          case 'greater_than':
            query = query.gt(filter.field, filter.value)
            break
          case 'less_than':
            query = query.lt(filter.field, filter.value)
            break
          case 'between':
            query = query.gte(filter.field, filter.value[0]).lte(filter.field, filter.value[1])
            break
          case 'in':
            query = query.in(filter.field, filter.value)
            break
        }
      })
    }

    // Apply sorting
    if (sortBy) {
      query = query.order(sortBy.field, { ascending: sortBy.ascending !== false })
    }

    // Apply limit
    if (limit) {
      query = query.limit(limit)
    }

    const { data, error } = await query

    if (error) throw error

    return {
      success: true,
      data,
      count: data?.length || 0,
      fields,
    }
  } catch (error) {
    console.error('Error building report:', error)
    return {
      success: false,
      error: error.message,
      data: [],
    }
  }
}

/**
 * Save report configuration
 */
export const saveReport = async (reportConfig, organizationId, userId) => {
  try {
    const { data, error } = await supabase
      .from('saved_reports')
      .insert({
        organization_id: organizationId,
        user_id: userId,
        report_name: reportConfig.name,
        report_config: reportConfig,
        created_at: new Date().toISOString(),
      })
      .select()
      .single()

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    console.error('Error saving report:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get saved reports
 */
export const getSavedReports = async (organizationId, userId = null) => {
  try {
    let query = supabase
      .from('saved_reports')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false })

    if (userId) {
      query = query.eq('user_id', userId)
    }

    const { data, error } = await query

    if (error) throw error
    return { success: true, data: data || [] }
  } catch (error) {
    console.error('Error getting saved reports:', error)
    return { success: false, error: error.message, data: [] }
  }
}

/**
 * Delete saved report
 */
export const deleteReport = async (reportId, organizationId) => {
  try {
    const { error } = await supabase
      .from('saved_reports')
      .delete()
      .eq('id', reportId)
      .eq('organization_id', organizationId)

    if (error) throw error
    return { success: true }
  } catch (error) {
    console.error('Error deleting report:', error)
    return { success: false, error: error.message }
  }
}

export default {
  REPORT_SOURCES,
  FIELD_TYPES,
  getAvailableFields,
  buildReport,
  saveReport,
  getSavedReports,
  deleteReport,
}

